// Client-side media encryption for Ping Prime §9 (AES-256-GCM).

export interface MediaBlobSecrets {
  readonly contentKey: Uint8Array;
  readonly contentIv: Uint8Array;
}

export interface EncryptedMediaBlob {
  readonly ciphertext: Uint8Array;
  readonly secrets: MediaBlobSecrets;
  /** SHA-256 of plaintext (32 bytes). */
  readonly hash: Uint8Array;
}

export interface BlobRefWire {
  readonly media_id: string;
  readonly content_key: string;
  readonly content_iv: string;
  readonly hash: string;
}

export function generateMediaSecrets(): MediaBlobSecrets {
  const contentKey = new Uint8Array(32);
  const contentIv = new Uint8Array(12);
  crypto.getRandomValues(contentKey);
  crypto.getRandomValues(contentIv);
  return { contentKey, contentIv };
}

export async function encryptMediaBlob(plaintext: Uint8Array): Promise<EncryptedMediaBlob> {
  const secrets = generateMediaSecrets();
  const hash = await sha256(plaintext);
  const ciphertext = await aesGcmEncrypt(plaintext, secrets.contentKey, secrets.contentIv);
  return { ciphertext, secrets, hash };
}

export async function decryptMediaBlob(
  ciphertext: Uint8Array,
  contentKey: Uint8Array,
  contentIv: Uint8Array,
): Promise<Uint8Array> {
  return aesGcmDecrypt(ciphertext, contentKey, contentIv);
}

export function blobRefToWire(mediaIdB64Url: string, secrets: MediaBlobSecrets, hash: Uint8Array): BlobRefWire {
  return {
    media_id: mediaIdB64Url,
    content_key: bytesToBase64Url(secrets.contentKey),
    content_iv: bytesToBase64Url(secrets.contentIv),
    hash: bytesToBase64Url(hash),
  };
}

export function parseBlobRefWire(ref: BlobRefWire): {
  mediaId: string;
  contentKey: Uint8Array;
  contentIv: Uint8Array;
  hash: Uint8Array;
} {
  return {
    mediaId: ref.media_id,
    contentKey: base64UrlToBytes(ref.content_key),
    contentIv: base64UrlToBytes(ref.content_iv),
    hash: base64UrlToBytes(ref.hash),
  };
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const ab = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(ab).set(bytes);
  return ab;
}

async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest('SHA-256', toArrayBuffer(data));
  return new Uint8Array(digest);
}

async function aesGcmEncrypt(
  plaintext: Uint8Array,
  keyBytes: Uint8Array,
  iv: Uint8Array,
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', toArrayBuffer(keyBytes), { name: 'AES-GCM' }, false, ['encrypt']);
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: toArrayBuffer(iv) }, key, toArrayBuffer(plaintext));
  return new Uint8Array(cipher);
}

async function aesGcmDecrypt(
  ciphertext: Uint8Array,
  keyBytes: Uint8Array,
  iv: Uint8Array,
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', toArrayBuffer(keyBytes), { name: 'AES-GCM' }, false, ['decrypt']);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(ciphertext),
  );
  return new Uint8Array(plain);
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function base64UrlToBytes(b64url: string): Uint8Array {
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad === 2) b64 += '==';
  else if (pad === 3) b64 += '=';
  else if (pad === 1) throw new Error('invalid base64url');
  return base64ToBytes(b64);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}
